import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormBuilder, Validators, FormArray, ReactiveFormsModule, FormsModule, FormGroup } from '@angular/forms';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { Guid } from 'guid-typescript';
import { FileUploadComponent } from '../../../../../common/components/file/file.component';
import { CostType, FundingSourceType } from '../../../../../common/models/common';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { FormattedNumberInputDirective } from '../../../../../common/directives/inputs/formatted-number.directive';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Project, ProjectBudget, ProjectBudgetItem, ProjectFundingSource, UpdateProjectBudget } from '../../../../../common/models/project';
import { ProjectService } from '../../../../../common/services/project.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BudgetStatusComponent } from './status/status.component';
import { forkJoin } from 'rxjs';



@Component({
    selector: 'app-project-budget',
    templateUrl: './budget.component.html',
    styleUrls: ['./budget.component.scss'],
    imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    FileUploadComponent,
    BudgetStatusComponent,
    FormattedNumberInputDirective
]
})
export class ProjectBudgetComponent implements OnInit {
  projectId!: string;
  projectApprovedAmount!: number;
  projectBudgetId!: string;

  projectBudgetForm!: FormGroup;

  // QueryLists for direct cost panels and their textareas
  @ViewChildren('directPanel', { read: MatExpansionPanel }) directExpansionPanels!: QueryList<MatExpansionPanel>;
  @ViewChildren('directDescriptionInputs') directDescriptionInputs!: QueryList<ElementRef<HTMLTextAreaElement>>;

  // QueryLists for indirect cost panels and their textareas
  @ViewChildren('indirectPanel', { read: MatExpansionPanel }) indirectExpansionPanels!: QueryList<MatExpansionPanel>;
  @ViewChildren('indirectDescriptionInputs') indirectDescriptionInputs!: QueryList<ElementRef<HTMLTextAreaElement>>;

  // QueryLists for funding panels and their amount input
  @ViewChildren('fundingPanel', { read: MatExpansionPanel }) fundingExpansionPanels!: QueryList<MatExpansionPanel>;
  @ViewChildren('fundingAmountInputs') fundingAmountInputs!: QueryList<ElementRef<HTMLInputElement>>;
  
  budgetType = CostType;
  fundingSourceType = FundingSourceType;
  private fundingSourceTypes: { type: FundingSourceType; label: string }[] = [
    { type: FundingSourceType.OwnFunds, label: 'I. Vlastiti izvori' },
    { type: FundingSourceType.OtherPublicAuthorities, label: 'II. Ostala tijela javne vlasti' },
    { type: FundingSourceType.LocalAndRegionalAuthorities, label: 'III. Jedinice lokalne i područne (regionalne) samouprave' },
    { type: FundingSourceType.Other, label: 'IV. Drugo' }
  ];

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };
  
  
  getFundingSourceLabel(type: FundingSourceType): string {
    return this.fundingSourceTypes.find(source => source.type === type)?.label || 'Nepoznato';
  }
  
  get directCosts(): FormArray {
    return this.projectBudgetForm?.get('directCosts') as FormArray;
  }

  get indirectCosts(): FormArray {
    return this.projectBudgetForm?.get('indirectCosts') as FormArray;
  }

  get projectFundingSources(): FormArray {
    return this.projectBudgetForm?.get('projectFundingSources') as FormArray;
  }

  get totalDirectCostsRequested(): number {
    return this.directCosts?.controls.reduce((sum, control) => {
      return sum + (control.get('amountRequested')?.value || 0);
    }, 0) || 0;
  }
  
  get totalDirectCostsActual(): number {
    return this.directCosts?.controls.reduce((sum, control) => {
      return sum + (control.get('totalAmount')?.value || 0);
    }, 0) || 0;
  }
  
  get totalIndirectCostsRequested(): number {
    return this.indirectCosts?.controls.reduce((sum, control) => {
      return sum + (control.get('amountRequested')?.value || 0);
    }, 0) || 0;
  }
  
  get totalIndirectCostsActual(): number {
    return this.indirectCosts?.controls.reduce((sum, control) => {
      return sum + (control.get('totalAmount')?.value || 0);
    }, 0) || 0;
  }
  
  
  get totalCostsRequested(): number {
    return this.totalDirectCostsRequested + this.totalIndirectCostsRequested;
  }
  
  get totalCostsActual(): number {
    return this.totalDirectCostsActual + this.totalIndirectCostsActual;
  }
  
  get totalFundingFromOtherSources(): number {
    return this.projectFundingSources.controls.reduce((sum, control) => {
      return sum + (control.get('amount')?.value || 0);
    }, 0);
  }
  
  get totalFunding(): number {
    return this.totalCostsRequested + this.totalFundingFromOtherSources;
  }

  constructor(
    private fb: FormBuilder, 
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private projectService: ProjectService
  ) { 
    this.projectBudgetForm = this.fb.group({
      id: [''],
      directCosts: this.fb.array([]),
      indirectCosts: this.fb.array([]),
      projectFundingSources: this.fb.array([])
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.projectId = params.get('projectId') || '';
      this.projectBudgetId = params.get('projectBudgetId') || '';

      if (this.projectId && this.projectBudgetId) {
        this.loadProjectBudget(this.projectId, this.projectBudgetId);
      }
    });
  }

  private loadProjectBudget(projectId: string, projectBudgetId: string): void {
     forkJoin({
      project: this.projectService.getProject(Guid.parse(projectId)),
      projectBudget: this.projectService.getProjectBudget(Guid.parse(projectId), Guid.parse(projectBudgetId))
    }).subscribe({
      next: ({ project, projectBudget }) => {
        this.projectApprovedAmount = project.approvedAmount;
        
        if (!projectBudget) {
          this.snackBar.open('Traženi projektni proračun nije pronađen.', 'Zatvori', this.snackbarConfig);
          return;
        }
        this.populateProjectBudget(ProjectBudget.fromJson(projectBudget));
      },
      error: (err) => {
        console.error('Error loading project budget', err);
        this.snackBar.open('Dogodila se greška prilikom učitavanja projektnog proračuna.', 'Zatvori', this.snackbarConfig);
      }
    });
  }

  private populateProjectBudget(projectBudget: ProjectBudget): void {
    this.directCosts.clear();
    this.indirectCosts.clear();
    this.projectFundingSources.clear();

    projectBudget.projectFundingSources.forEach(source => {
      this.addProjectFundingSource(source.type, source);
    });

    projectBudget.projectBudgetItems.forEach(item => {
      this.addProjectBudgetItem(item.type, item, false);
    });
  }

  addProjectBudgetItem(type: CostType, item?: ProjectBudgetItem, autoFocus: boolean = true): void {
    const projectBudgetItemForm = this.fb.group({
      id: [item?.id || Guid.EMPTY],
      budgetItemId: [item?.budgetItemId || null],
      type: [type, Validators.required],
      description: [item?.description || '', [Validators.required, Validators.maxLength(500)]],
      amountRequested: [item?.amountRequested || 0, [Validators.required, Validators.min(0)]],
      totalAmount: [item?.totalAmount || 0, [Validators.required, Validators.min(0)]],
      explanation: [item?.explanation || '', [Validators.required, Validators.maxLength(1000)]],
      fileStorageLocation: [item?.fileStorageLocation || `projectbudgetitems/${Guid.create().toString()}`]
    });
  
    if (type === CostType.Direct) {
      this.directCosts.push(projectBudgetItemForm);
    } else {
      this.indirectCosts.push(projectBudgetItemForm);
    }

    setTimeout(() => {
      if (!autoFocus) return;

      if (type === CostType.Direct && this.directExpansionPanels.length > 0) {
        const panel = this.directExpansionPanels.last;
        panel.open();
        setTimeout(() => {
          const lastInput = this.directDescriptionInputs.last?.nativeElement;
          lastInput?.focus();
        }, 50);
      } else if (type === CostType.Indirect && this.indirectExpansionPanels.length > 0) {
        const panel = this.indirectExpansionPanels.last;
        panel.open();
        setTimeout(() => {
          const lastInput = this.indirectDescriptionInputs.last?.nativeElement;
          lastInput?.focus();
        }, 50);
      }
    }, 50);
  }

  removeProjectBudgetItem(type: CostType, index: number): void {
    if (type === CostType.Direct) {
      this.directCosts.removeAt(index);
    } else {
      this.indirectCosts.removeAt(index);
    }
    this.snackBar.open('Stavka troška uklonjena.', 'Zatvori', this.snackbarConfig);
  }


  addProjectFundingSource(sourceType: FundingSourceType, source?: ProjectFundingSource): void {
    const fundingSourceForm = this.fb.group({
      id: [source?.id || ''],
      type: [sourceType, Validators.required],
      amount: [source?.amount || 0, [Validators.required, Validators.min(0)]]
    });
    this.projectFundingSources.push(fundingSourceForm);
  }

  closePanel(panel: MatExpansionPanel): void {
    panel.close();
  }

  onSubmit(): void {
    if (!this.projectBudgetForm.valid) {
      this.snackBar.open('Molimo popunite sve obavezne podatke.', 'Zatvori', this.snackbarConfig);
      return;
    }

    const updateDto: UpdateProjectBudget = {
      projectBudgetItems: [...this.directCosts.value, ...this.indirectCosts.value],
      projectFundingSources: this.projectFundingSources.value
    };

    this.projectService.updateProjectBudget(Guid.parse(this.projectId), Guid.parse(this.projectBudgetId), updateDto)
      .subscribe({
        next: () => {
          this.snackBar.open('Projektni proračun je uspješno ažuriran.', 'Zatvori', this.snackbarConfig);
          this.router.navigate(['association', 'projects', this.projectId, 'budgets']);
        },
        error: (err) => {
          console.error('Error updating project budget', err);
          this.snackBar.open('Dogodila se greška prilikom spremanja projektnog proračuna.', 'Zatvori', this.snackbarConfig);
        }
      });
  }

}
